import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import WhatsAppButton from "../../components/feature/WhatsAppButton";

const WA_LINK = "https://wa.me/50766132697?text=Hola%2C%20quiero%20agendar%20una%20cita%20con%20el%20Dr.%20Erick%20Tejada";

const timeline = [
  {
    year: "Formación Médica",
    title: "Médico Cirujano",
    description: "Formación médica integral con énfasis en obstetricia y ginecología, adquiriendo las bases para una práctica clínica centrada en la paciente.",
  },
  {
    year: "Especialización",
    title: "Ginecología y Obstetricia",
    description: "Especialidad médica en ginecología y obstetricia, con entrenamiento en procedimientos diagnósticos y terapéuticos avanzados de la salud femenina.",
  },
  {
    year: "Subespecialización",
    title: "Colposcopia y VPH",
    description: "Formación avanzada en colposcopia diagnóstica y manejo del Virus del Papiloma Humano, con protocolos internacionales de detección temprana.",
  },
  {
    year: "Actualización Continua",
    title: "Medicina Preventiva",
    description: "Participación activa en congresos médicos y actualización permanente en las últimas guías internacionales de prevención del cáncer cervicouterino.",
  },
];

const values = [
  {
    icon: "ri-heart-line",
    title: "Vocación de servicio",
    description: "Cada consulta es una oportunidad de cuidar una vida. Tratamos a cada paciente con la misma dedicación y respeto que nos gustaría recibir.",
  },
  {
    icon: "ri-lightbulb-line",
    title: "Claridad y educación",
    description: "Creemos que una paciente bien informada toma mejores decisiones. Explicamos cada procedimiento en un lenguaje claro y accesible.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Prevención ante todo",
    description: "La prevención a tiempo puede salvar vidas. Nuestro enfoque está en detectar antes de que avance, no en tratar lo que pudo evitarse.",
  },
  {
    icon: "ri-user-heart-line",
    title: "Atención personalizada",
    description: "Cada mujer es única. Adaptamos nuestro enfoque y recomendaciones a la historia, necesidades y contexto de vida de cada paciente.",
  },
];

export default function About() {
  const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://example.com";
  const pageUrl = `${siteUrl}/sobre-el-doctor`;

  const schemaPerson = {
    "@context": "https://schema.org",
    "@type": "Physician",
    "name": "Dr. Erick Tejada",
    "jobTitle": "Ginecólogo y Colposcopista",
    "description": "Médico especializado en ginecología, colposcopia y manejo del VPH, con enfoque en la prevención y detección temprana del cáncer cervicouterino en Panamá.",
    "url": pageUrl,
    "image": "https://static.readdy.ai/image/0538348e5f7265469219be823f4b4c0f/742424fab0dc34062957b4a9bfa06d4f.png",
    "telephone": "+507-6613-2697",
    "sameAs": ["https://www.instagram.com/drericktejada/"],
    "worksFor": {
      "@type": "MedicalClinic",
      "name": "Clínica de Colposcopia Dr. Erick Tejada",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "El Dorado, al lado del Do It Center",
        "addressLocality": "Ciudad de Panamá",
        "addressCountry": "PA"
      }
    },
    "medicalSpecialty": "Gynecology",
    "knowsAbout": ["Colposcopia", "VPH - Virus del Papiloma Humano", "Papanicolaou", "Ginecología Preventiva", "Cáncer Cervicouterino"]
  };

  const schemaBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": siteUrl },
      { "@type": "ListItem", "position": 2, "name": "Sobre el Doctor", "item": pageUrl }
    ]
  };

  return (
    <>
      <title>Dr. Erick Tejada - Ginecólogo Colposcopista en Panamá | Sobre el Doctor</title>
      <meta name="description" content="Conoce al Dr. Erick Tejada, ginecólogo y colposcopista especializado en detección de VPH y prevención del cáncer cervicouterino en Ciudad de Panamá. Más de 10 años de experiencia. Agenda tu cita: +507 6613-2697." />
      <meta name="keywords" content="Dr. Erick Tejada ginecólogo Panamá, colposcopista Panamá, especialista VPH Panamá, ginecología Ciudad de Panamá" />
      <link rel="canonical" href={pageUrl} />
      <meta property="og:type" content="profile" />
      <meta property="og:title" content="Dr. Erick Tejada - Ginecólogo Colposcopista en Panamá" />
      <meta property="og:description" content="Ginecólogo especializado en colposcopia y detección de VPH en Ciudad de Panamá. Más de 10 años de experiencia en ginecología preventiva." />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content="https://static.readdy.ai/image/0538348e5f7265469219be823f4b4c0f/742424fab0dc34062957b4a9bfa06d4f.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Dr. Erick Tejada - Ginecólogo Colposcopista Panamá" />
      <meta name="twitter:description" content="Especialista en colposcopia y VPH en Ciudad de Panamá. Más de 10 años de experiencia." />
      <script type="application/ld+json">{JSON.stringify(schemaPerson)}</script>
      <script type="application/ld+json">{JSON.stringify(schemaBreadcrumb)}</script>
      <main>
        <Navbar />

        {/* Hero */}
        <section className="relative pt-20 pb-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900 to-slate-800" />
          <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 pt-8 pb-16 md:pb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <span
                  className="inline-flex items-center gap-2 text-teal-300 text-xs md:text-sm font-medium mb-5"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  <i className="ri-stethoscope-line"></i>
                  Especialista en Colposcopia · Ciudad de Panamá
                </span>
                <h1
                  className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-3 md:mb-4"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Dr. Erick Tejada
                </h1>
                <p
                  className="text-teal-300 text-lg md:text-xl mb-5 md:mb-6 italic"
                  style={{ fontFamily: "'Lora', serif" }}
                >
                  Ginecólogo &amp; Colposcopista
                </p>
                <p
                  className="text-white/75 text-sm md:text-base leading-relaxed mb-7 md:mb-8"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Médico especializado en ginecología, colposcopia y manejo del VPH, con un enfoque profundo en la prevención y detección temprana del cáncer cervicouterino en Panamá.
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                  <a
                    href={WA_LINK}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-full transition-all text-sm whitespace-nowrap cursor-pointer w-full sm:w-auto"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    <i className="ri-whatsapp-line"></i>
                    Agendar Cita
                  </a>
                  <a
                    href="https://www.instagram.com/drericktejada/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold px-6 py-3 rounded-full transition-all text-sm whitespace-nowrap cursor-pointer w-full sm:w-auto"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    <i className="ri-instagram-line"></i>
                    Instagram
                  </a>
                </div>
              </div>

              {/* Doctor image */}
              <div className="flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="w-60 sm:w-72 md:w-80 h-80 sm:h-96 md:h-[440px] rounded-3xl overflow-hidden border-4 border-teal-400/30 mx-auto">
                    <img
                      src="https://static.readdy.ai/image/0538348e5f7265469219be823f4b4c0f/742424fab0dc34062957b4a9bfa06d4f.png"
                      alt="Dr. Erick Tejada - Ginecólogo Colposcopista Ciudad de Panamá"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-5 left-2 bg-white rounded-2xl px-4 py-3 border border-slate-100">
                    <p className="text-teal-600 font-bold text-xl" style={{ fontFamily: "'Poppins', sans-serif" }}>10+</p>
                    <p className="text-slate-600 text-xs" style={{ fontFamily: "'Poppins', sans-serif" }}>años de experiencia</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy */}
        <section className="py-12 md:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
              {/* Philosophy text */}
              <div>
                <span className="inline-block text-teal-600 text-xs md:text-sm font-semibold uppercase tracking-wider mb-4"
                  style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Filosofía de atención
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mb-5"
                  style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Medicina con{" "}
                  <span className="italic font-normal text-teal-600" style={{ fontFamily: "'Lora', serif" }}>
                    propósito y humanidad
                  </span>
                </h2>
                <div className="space-y-4 text-slate-600 text-sm md:text-base leading-relaxed"
                  style={{ fontFamily: "'Poppins', sans-serif" }}>
                  <p>
                    El Dr. Erick Tejada ejerce la medicina con la convicción de que <strong>la prevención es la mejor herramienta de salud</strong>. Su especialización en colposcopia y VPH responde a una realidad clara: muchas mujeres desarrollan cáncer cervicouterino porque las lesiones precancerosas no fueron detectadas a tiempo.
                  </p>
                  <p>
                    Su enfoque va más allá del diagnóstico. Cada consulta incluye educación: explica qué es el VPH, cómo funciona el sistema inmune frente a él, qué significa cada resultado y cuáles son los próximos pasos. Porque una paciente informada es una paciente empoderada.
                  </p>
                  <p>
                    Con un trato cálido y profesional, el Dr. Tejada crea un ambiente de confianza donde cada mujer puede expresar sus dudas y preocupaciones sin sentirse juzgada. La salud femenina merece atención especializada, cercana y sin barreras.
                  </p>
                </div>
              </div>

              {/* Values */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {values.map((v) => (
                  <div key={v.title} className="bg-slate-50 rounded-2xl p-5 md:p-6 border border-slate-100">
                    <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-xl bg-teal-100 mb-3 md:mb-4">
                      <i className={`${v.icon} text-teal-600 text-lg md:text-xl`}></i>
                    </div>
                    <h3 className="font-semibold text-slate-800 text-sm mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {v.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {v.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Timeline / Formation */}
        <section className="py-12 md:py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block text-teal-600 text-xs md:text-sm font-semibold uppercase tracking-wider mb-3"
                style={{ fontFamily: "'Poppins', sans-serif" }}>
                Trayectoria profesional
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800"
                style={{ fontFamily: "'Poppins', sans-serif" }}>
                Formación y especialización
              </h2>
            </div>

            <div className="relative">
              <div className="hidden lg:block absolute left-1/2 -translate-x-0.5 top-0 bottom-0 w-0.5 bg-teal-200" />
              <div className="flex flex-col gap-6 md:gap-8">
                {timeline.map((item, i) => (
                  <div
                    key={item.title}
                    className={`flex flex-col lg:flex-row gap-4 lg:gap-0 items-start lg:items-center ${i % 2 === 0 ? "lg:flex-row-reverse" : ""}`}
                  >
                    <div className={`lg:w-1/2 w-full ${i % 2 === 0 ? "lg:pl-10" : "lg:pr-10 lg:text-right"}`}>
                      <div className={`bg-white rounded-2xl p-5 md:p-6 border border-slate-100 ${i % 2 !== 0 ? "lg:ml-auto" : ""}`} style={{ maxWidth: "480px" }}>
                        <span className="inline-block text-xs font-semibold text-teal-600 bg-teal-50 px-3 py-1 rounded-full mb-3"
                          style={{ fontFamily: "'Poppins', sans-serif" }}>
                          {item.year}
                        </span>
                        <h3 className="font-bold text-slate-800 text-sm md:text-base mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                          {item.title}
                        </h3>
                        <p className="text-slate-500 text-sm leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <div className="hidden lg:flex w-0 relative z-10 justify-center">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-600 border-4 border-white" style={{ marginLeft: "-10px" }} />
                    </div>
                    <div className="lg:w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-12 md:py-24 bg-white">
          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block text-teal-600 text-xs md:text-sm font-semibold uppercase tracking-wider mb-3"
                style={{ fontFamily: "'Poppins', sans-serif" }}>
                Contacto &amp; Citas
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mb-4"
                style={{ fontFamily: "'Poppins', sans-serif" }}>
                Agenda tu consulta hoy
              </h2>
              <p className="text-slate-500 max-w-lg mx-auto text-sm md:text-base"
                style={{ fontFamily: "'Poppins', sans-serif" }}>
                La atención es con cita previa. El método más rápido y directo es por WhatsApp.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {/* WhatsApp Big CTA */}
              <a
                href={WA_LINK}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-4 bg-green-50 border-2 border-green-400 hover:bg-green-100 rounded-2xl px-5 md:px-7 py-5 md:py-6 transition-all cursor-pointer group"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-2xl bg-green-500 group-hover:bg-green-600 transition-colors flex-shrink-0">
                  <i className="ri-whatsapp-line text-white text-2xl md:text-3xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-green-800 text-base md:text-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    Agendar por WhatsApp
                  </p>
                  <p className="text-green-700 text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    +507 6613-2697 · Respuesta rápida
                  </p>
                </div>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-line text-green-600 text-lg"></i>
                </div>
              </a>

              {/* Info cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {[
                  {
                    icon: "ri-phone-line",
                    color: "bg-teal-100",
                    iconColor: "text-teal-600",
                    title: "Teléfono",
                    content: "+507 6613-2697",
                    href: "tel:+50766132697",
                  },
                  {
                    icon: "ri-map-pin-line",
                    color: "bg-emerald-100",
                    iconColor: "text-emerald-600",
                    title: "Ubicación",
                    content: "El Dorado, al lado del Do It center, Ciudad de Panamá, Panamá",
                    href: undefined,
                  },
                  {
                    icon: "ri-time-line",
                    color: "bg-amber-100",
                    iconColor: "text-amber-600",
                    title: "Horario de Atención",
                    content: "Lunes a Viernes: 9:00 AM – 5:00 PM\nAtención con cita previa",
                    href: undefined,
                  },
                  {
                    icon: "ri-instagram-line",
                    color: "bg-rose-100",
                    iconColor: "text-rose-500",
                    title: "Instagram",
                    content: "@drericktejada",
                    href: "https://www.instagram.com/drericktejada/",
                  },
                  {
                    icon: "ri-money-dollar-circle-line",
                    color: "bg-slate-100",
                    iconColor: "text-slate-600",
                    title: "Forma de Pago",
                    content: "Efectivo y Yappy. No se aceptan tarjetas de crédito ni débito.",
                    href: undefined,
                  },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3 bg-slate-50 rounded-xl px-4 md:px-5 py-4 border border-slate-100">
                    <div className={`w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-xl ${item.color} flex-shrink-0 mt-0.5`}>
                      <i className={`${item.icon} ${item.iconColor} text-base md:text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm mb-0.5" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        {item.title}
                      </p>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.href.startsWith("http") ? "_blank" : undefined}
                          rel={item.href.startsWith("http") ? "nofollow noopener noreferrer" : undefined}
                          className="text-slate-600 text-sm hover:text-teal-600 transition-colors cursor-pointer whitespace-pre-line break-words"
                          style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-slate-600 text-sm whitespace-pre-line break-words" style={{ fontFamily: "'Poppins', sans-serif" }}>
                          {item.content}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="rounded-2xl overflow-hidden border border-slate-100 h-64 md:h-80">
                <iframe
                  title="Ubicación Clínica de Colposcopia Dr. Erick Tejada - El Dorado, Ciudad de Panamá"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d491.8!2d-79.5365202!3d9.0069777!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8faca9004f2fa099%3A0x6776dd1fc2956426!2sCl%C3%ADnica%20de%20Colposcopia%20Dr.%20Erick%20Tejada!5e0!3m2!1ses!2spa!4v1700000000000!5m2!1ses!2spa"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <WhatsAppButton />
      </main>
    </>
  );
}
